/**
 * [비즈니스 목적]
 * 전체 애플리케이션의 레이아웃 프레임워크 컴포넌트
 *
 * [워크플로우]
 * 1. 사용자 권한에 따라 적절한 사이드바 선택 (Admin / Tenant / Default)
 * 2. 상단 AppBar에 사용자 프로필, 로그아웃, 테넌트 선택 표시
 * 3. 반응형 레이아웃: 모바일에서는 Drawer, 데스크톱에서는 고정 사이드바
 * 4. 메인 콘텐츠 영역에 children 렌더링
 *
 * [주요 기능]
 * 1. 반응형 레이아웃
 *    - Desktop (1920px 이상): 320px 사이드바
 *    - Desktop (< 1920px): 280px 사이드바
 *    - Mobile: Temporary Drawer (터치/클릭으로 열기)
 *
 * 2. 권한별 사이드바
 *    - Admin: AdminSidebar (시스템 관리 메뉴)
 *    - Tenant User: TenantSidebar (테넌트 전용 메뉴)
 *    - Default: Sidebar (기본 메뉴)
 *
 * 3. 글로벌 테넌트 선택 (관리자 전용)
 *    - 관리자가 여러 테넌트 데이터를 관리할 수 있도록 선택 UI 제공
 *
 * 4. 사용자 메뉴
 *    - 프로필 정보 표시
 *    - Settings 페이지 이동
 *    - 로그아웃
 *
 * [Props]
 * - children: 메인 콘텐츠 영역에 렌더링할 컴포넌트
 * - isAdmin: 관리자 모드 활성화 여부
 *
 * [상태 관리]
 * - mobileOpen: 모바일 Drawer 열림/닫힘 상태
 * - anchorEl: 프로필 메뉴 앵커 엘리먼트
 *
 * [반응형 브레이크포인트]
 * - xs: < 600px
 * - sm: 600px ~ 960px
 * - md: 960px ~ 1280px (Drawer 전환점)
 * - lg: 1280px ~ 1920px
 * - xl: >= 1920px
 *
 * [사용 예시]
 * <Layout isAdmin={true}>
 *   <Dashboard />
 * </Layout>
 */

import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Settings,
  Logout,
  Dashboard,
  Storage,
  Description,
  Group,
  Category,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import AdminSidebar from './AdminSidebar';
import TenantSidebar from './TenantSidebar';
import GlobalTenantSelector from './GlobalTenantSelector';
import { useNavigate } from 'react-router-dom';

// Desktop-first responsive drawer width
const drawerWidth = window.innerWidth >= 1920 ? 320 : 280;

const Layout = ({ children, isAdmin = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { currentUser, userProfile, logout, isAdmin: checkIsAdmin, isTenantUser } = useAuth();
  const navigate = useNavigate();

  // Determine which sidebar to use
  const getSidebarComponent = () => {
    if (isAdmin && checkIsAdmin()) {
      return AdminSidebar;
    } else if (!isAdmin && isTenantUser()) {
      return TenantSidebar;
    }
    return Sidebar; // Fallback
  };

  const SidebarComponent = getSidebarComponent();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleProfileMenuClose();
  };

  const handleProfileSettings = () => {
    navigate('/settings');
    handleProfileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'primary.main',
          // Desktop-optimized app bar
          height: { xs: 64, lg: 72 }, // Taller on desktop
          '& .MuiToolbar-root': {
            minHeight: { xs: 64, lg: 72 },
            padding: { xs: '0 16px', lg: '0 24px' }
          }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              noWrap 
              component="div"
              sx={{
                fontSize: { xs: '1.25rem', lg: '1.5rem' }, // Larger text on desktop
                fontWeight: 600
              }}
            >
              Ontology Data Dashboard
            </Typography>
          </Box>

          {/* Tenant Selection for Admin */}
          {isAdmin && checkIsAdmin && checkIsAdmin() && (
            <GlobalTenantSelector />
          )}

          {/* User Profile Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {userProfile?.displayName || currentUser?.displayName || 'User'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              {currentUser?.photoURL ? (
                <Avatar
                  src={currentUser.photoURL}
                  alt={currentUser.displayName}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <SidebarComponent onClose={handleDrawerToggle} isAdmin={isAdmin} />
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          <SidebarComponent isAdmin={isAdmin} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          // Desktop-optimized layout
          '@media (min-width: 1200px)': {
            padding: 0,
          }
        }}
      >
        <Toolbar /> {/* Spacer for fixed app bar */}
        <Box 
          sx={{ 
            p: { xs: 2, sm: 3, lg: 4, xl: 5 }, // Responsive padding
            maxWidth: { xl: '1600px' }, // Maximum content width for ultra-wide screens
            margin: { xl: '0 auto' }, // Center content on ultra-wide screens
            // Better container spacing for desktop
            '@media (min-width: 1920px)': {
              padding: '2rem 3rem',
            }
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;